import { useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { inputsMaxLengths } from '../../../constants';
import styles from './FormContent.module.scss';
import { Button, DateInput, Input, RolePermissionChecker, Select } from 'components';
import { appActions } from 'store/app/slice';
import { selectCurrentPatientCase } from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { getClinics } from 'store/users/selectors';
import { useDispatch, useSelector } from 'store/utils';
import { UserRole } from 'types';
import { onlyLatinLettersSymbolsNumbers } from 'utils';

const schema = yup.object({
  externalPatientId: yup
    .string()
    .test('isNumber', 'Numeric field', (value) => {
      return /^[0-9]*$/.test(value || '');
    })
    .typeError('Numeric field')
    .required('The field is empty'),
  firstname: yup
    .string()
    .required('The field is empty')
    .matches(onlyLatinLettersSymbolsNumbers, {
      message: 'Only Latin Alphabet is allowed',
    })
    .matches(/^(?!\s)(?!.*\s$)/, {
      message: 'No leading and trailing spaces are allowed',
    }),
  lastname: yup
    .string()
    .required('The field is empty')
    .matches(onlyLatinLettersSymbolsNumbers, {
      message: 'Only Latin Alphabet is allowed',
    })
    .matches(/^(?!\s)(?!.*\s$)/, {
      message: 'No leading and trailing spaces are allowed',
    }),
  oht: yup.string().required('The field is empty'),
  date: yup.string().required('The field is empty'),
  clinicId: yup.string().optional(),
});

type Props = {
  setDisplayError(value: boolean): void;
  onClose(): void;
};

export const FormContent = ({ onClose, setDisplayError }: Props) => {
  const [isLoading, setIsLoading] = useState(false);

  const clinics = useSelector(getClinics);
  const currentPatientCase = useSelector(selectCurrentPatientCase) ?? {
    firstName: '',
    lastName: '',
    oralHealthTherapist: '',
    dateOfBirth: '',
    id: -1,
    externalPatientId: -1,
    clinicId: '',
  };

  const { firstName, lastName, oralHealthTherapist, id, dateOfBirth, externalPatientId, clinicId } = currentPatientCase;

  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid, isDirty, dirtyFields },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstname: firstName,
      lastname: lastName,
      oht: oralHealthTherapist,
      date: dateOfBirth.split('-').reverse().join('/'),
      externalPatientId: String(externalPatientId),
      clinicId,
    },
  });

  const watchedId = watch('externalPatientId');
  const watchedFirstname = watch('firstname');
  const watchedLastname = watch('lastname');
  const watchedOht = watch('oht');
  const watchedDate = watch('date');
  const watchedClinic = watch('clinicId');

  const onSubmit = handleSubmit(async (formData) => {
    if (isLoading) return;

    if (!isDirty) {
      onClose();
      return;
    }

    const { firstname, lastname, oht, date, externalPatientId, clinicId } = formData;
    try {
      setIsLoading(true);
      const wrappedResult = dispatch(
        patientsActions.updatePatientCase({
          patientCaseData: {
            firstName: firstname,
            lastName: lastname,
            oralHealthTherapist: oht,
            dateOfBirth: date.split('/').reverse().join('-'),
            externalPatientId: Number(externalPatientId),
            ...(dirtyFields.clinicId && { clinicId: clinicId }),
          },
          patientCaseId: id,
        }),
      );
      await wrappedResult.unwrap();

      onClose();
      dispatch(appActions.setModalErrorText({ name: 'editPatientError', errorText: '' }));
    } catch (error: any) {
      dispatch(appActions.setModalErrorText({ name: 'editPatientError', errorText: error?.title }));
      setDisplayError(true);
    } finally {
      setIsLoading(false);
    }
  });

  const clinicOptions = useMemo(
    () =>
      clinics.map((clinic) => ({
        value: clinic.id,
        label: clinic.name,
      })),
    [clinics],
  );

  return (
    <div className={styles.formContainer}>
      <form className={styles.form}>
        <RolePermissionChecker rolesAllowedToView={[UserRole.ADMIN]}>
          <Input
            inputName="externalPatientId"
            labelText="ID"
            register={register}
            registerOptions={{ required: true, maxLength: inputsMaxLengths.id }}
            autoComplete="off"
            placeholder="Enter ID from patient management system"
            maxLength={inputsMaxLengths.id}
            currentValue={watchedId}
            setValue={setValue}
            errorMsg={errors.externalPatientId?.message}
            characterCounter
          />
        </RolePermissionChecker>
        <Input
          inputName="firstname"
          labelText="First name"
          register={register}
          registerOptions={{ required: true, maxLength: inputsMaxLengths.firstname }}
          autoComplete="off"
          placeholder="Enter patient first name"
          maxLength={inputsMaxLengths.firstname}
          currentValue={watchedFirstname}
          setValue={setValue}
          errorMsg={errors.firstname?.message}
          characterCounter
        />
        <Input
          inputName="lastname"
          labelText="Last name"
          register={register}
          registerOptions={{ required: true, maxLength: inputsMaxLengths.lastname }}
          autoComplete="off"
          placeholder="Enter patient last name"
          maxLength={inputsMaxLengths.lastname}
          currentValue={watchedLastname}
          setValue={setValue}
          errorMsg={errors.lastname?.message}
          characterCounter
        />
        <DateInput
          inputName="date"
          labelText="Date of birth"
          register={register}
          setValue={(value: string) => setValue('date', value, { shouldDirty: true })}
          errorMsg={errors.date?.message}
          triggerValidation={() => trigger('date')}
          defaultValue={watchedDate}
        />
        <Input
          inputName="oht"
          labelText="OHT"
          register={register}
          registerOptions={{ required: true, maxLength: inputsMaxLengths.oht }}
          autoComplete="off"
          placeholder="Enter oral health therapist name"
          maxLength={inputsMaxLengths.oht}
          currentValue={watchedOht}
          setValue={setValue}
          errorMsg={errors.oht?.message}
          characterCounter
        />
        <RolePermissionChecker rolesAllowedToView={[UserRole.ADMIN]}>
          <Select
            register={register}
            labelText="Clinic"
            value={watchedClinic}
            selectName="clinicId"
            options={clinicOptions}
          />
        </RolePermissionChecker>
      </form>
      <div className={styles.buttonRow}>
        <Button isLink onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={onSubmit} isDisabled={!isValid}>
          Save
        </Button>
      </div>
    </div>
  );
};
